
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { User } from "@supabase/supabase-js";
import { Opportunity } from "@/types/database-extensions";

interface UseOpportunityActionsProps {
  setCurrentOpportunity: (opportunity: Partial<Opportunity>) => void;
  setIsDialogOpen: (isOpen: boolean) => void;
  user: User | null;
}

export const useOpportunityActions = ({
  setCurrentOpportunity,
  setIsDialogOpen,
  user
}: UseOpportunityActionsProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const handleEdit = (opportunity: Opportunity) => {
    setCurrentOpportunity(opportunity);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to manage opportunities",
          variant: "destructive",
        });
        return;
      }

      setIsDeleting(true);
      
      // Check if user has admin role
      const { data: isAdmin } = await supabase
        .from("user_roles")
        .select("*")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();
        
      // Check if user owns the opportunity
      const { data: opportunity } = await supabase
        .from("opportunities")
        .select("user_id")
        .eq("id", id)
        .single();
      
      // Only allow delete if user is admin or the creator of the opportunity
      if (!(isAdmin || (opportunity && opportunity.user_id === user.id))) {
        throw new Error("You don't have permission to delete this opportunity");
      }

      const { error } = await supabase.from("opportunities").delete().eq("id", id);

      if (error) throw error;

      toast({
        title: "Deleted",
        description: "Opportunity has been removed",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    handleEdit,
    handleDelete,
    isDeleting
  };
};
